import React, { useState, useEffect } from "react";
import {
  useCreateCommunityEventMutation,
  useUpdateCommunityEventMutation,
  useGetAllCompaniesQuery,
  useGetCompanyEmployeesForEventQuery,
} from "../../../services/api";
import { getResourceIcon } from "../../../Components/icons/CommunityIcons";
import ErrorMessage from "../../../Components/common/ErrorMessage";
import RecurrenceSelector from "./RecurrenceSelector";
import "./CommunityEventForm.css";

const IconClose = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconPlus = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconTrash = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconEdit = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PLATFORM_OPTIONS = [
  { value: "zoom", label: "Zoom" },
  { value: "google_meet", label: "Google Meet" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "other", label: "Other" },
];
const RESOURCE_TYPES = ["Google Slide", "Google Doc", "Google Drive", "PDF", "Link"];

// Helper function to validate URL format
const isValidUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export default function CommunityEventForm({ event, onClose }) {
  const isEditMode = !!event;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    nextEventDate: "",
    startTime: "12:00",
    endTime: "12:00",
    meetingLink: "",
    meetingPlatform: "zoom",
  });

  // Recurrence state - Google Calendar style
  const [recurrenceRule, setRecurrenceRule] = useState({ frequency: "none" });

  const [resources, setResources] = useState([]);
  const [newResource, setNewResource] = useState({ title: "", type: "Google Slide", url: "" });
  const [showAddResource, setShowAddResource] = useState(false);
  const [editingResourceIndex, setEditingResourceIndex] = useState(null); // null = adding new, number = editing that index
  const [validationError, setValidationError] = useState("");

  // Company and employee selection state
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAllEmployees, setSelectAllEmployees] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");

  // Fetch companies for dropdown
  const { data: companiesData } = useGetAllCompaniesQuery({ limit: 100 });
  const companies = companiesData?.companies || [];

  // Fetch employees when company is selected
  const { data: employees = [], isLoading: employeesLoading } = useGetCompanyEmployeesForEventQuery(
    selectedCompanyId,
    { skip: !selectedCompanyId }
  );

  const [createEvent, { isLoading: isCreating, error: createError }] =
    useCreateCommunityEventMutation();
  const [updateEvent, { isLoading: isUpdating, error: updateError }] =
    useUpdateCommunityEventMutation();

  // Load event data in edit mode
  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || "",
        description: event.description || "",
        nextEventDate: event.nextEventDate
          ? new Date(event.nextEventDate).toISOString().split("T")[0]
          : "",
        startTime: event.startTime || "",
        endTime: event.endTime || "",
        meetingLink: event.meetingLink || "",
        meetingPlatform: event.meetingPlatform || "zoom",
      });
      setResources(event.resources || []);
      // Load company and employee selections
      setSelectedCompanyId(event.company?._id || event.company || "");
      setSelectedEmployees(
        (event.allowedEmployees || []).map((e) => e._id || e)
      );
      setSelectAllEmployees(event.selectAllEmployees || false);

      // Load recurrence data - convert from backend format
      if (event.recurrenceRule && event.cadence !== "One-Time") {
        const rule = event.recurrenceRule;
        // Map backend type to frontend frequency
        let frequency = rule.type || "weekly";
        if (rule.type === "custom_interval") {
          frequency = "weekly";
        } else if (rule.type === "daily") {
          frequency = "daily";
        } else if (rule.type === "yearly") {
          frequency = "yearly";
        }

        // Determine endType from stored data
        let endType = rule.endType || "after";
        if (rule.endDate) {
          endType = "on";
        } else if (rule.occurrences) {
          endType = "after";
        }

        setRecurrenceRule({
          frequency,
          interval: rule.interval || 1,
          daysOfWeek: rule.daysOfWeek || [],
          endType,
          occurrences: rule.occurrences || 13,
          endDate: rule.endDate || null,
        });
      } else {
        setRecurrenceRule({ frequency: "none" });
      }
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationError) setValidationError("");
  };

  const handleAddResource = () => {
    if (!newResource.title.trim()) {
      setValidationError("Resource title is required");
      return;
    }
    if (!newResource.url.trim()) {
      setValidationError("Resource URL is required");
      return;
    }
    if (!isValidUrl(newResource.url.trim())) {
      setValidationError("Please enter a valid URL (e.g., https://docs.google.com/...)");
      return;
    }

    if (editingResourceIndex !== null) {
      // Editing existing resource
      const updatedResources = [...resources];
      updatedResources[editingResourceIndex] = { ...newResource };
      setResources(updatedResources);
      setEditingResourceIndex(null);
    } else {
      // Adding new resource
      setResources([...resources, { ...newResource }]);
    }

    setNewResource({ title: "", type: "Google Slide", url: "" });
    setShowAddResource(false);
    setValidationError("");
  };

  const handleEditResource = (index) => {
    const resourceToEdit = resources[index];
    setNewResource({
      title: resourceToEdit.title,
      type: resourceToEdit.type,
      url: resourceToEdit.url,
    });
    setEditingResourceIndex(index);
    setShowAddResource(true);
    setValidationError("");
  };

  const handleCancelResourceEdit = () => {
    setShowAddResource(false);
    setNewResource({ title: "", type: "Google Slide", url: "" });
    setEditingResourceIndex(null);
    setValidationError("");
  };

  const handleRemoveResource = (index) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  // Derive available roles from employees
  const availableRoles = [...new Set(employees.map((emp) => emp.companyRole).filter(Boolean))].sort();

  // Filter employees by role
  const filteredEmployees = roleFilter === "all"
    ? employees
    : employees.filter((emp) => emp.companyRole === roleFilter);

  // Handle company change - reset employee selection
  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    setSelectedEmployees([]);
    setSelectAllEmployees(false);
    setRoleFilter("all");
  };

  // Handle "Select All" checkbox - adds/removes only the currently filtered employees
  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    if (checked && filteredEmployees.length > 0) {
      const filteredIds = filteredEmployees.map((emp) => emp._id);
      const merged = [...new Set([...selectedEmployees, ...filteredIds])];
      setSelectedEmployees(merged);
      setSelectAllEmployees(merged.length === employees.length);
    } else {
      // Uncheck: remove only the filtered employees from selection
      const filteredIds = new Set(filteredEmployees.map((emp) => emp._id));
      setSelectedEmployees(selectedEmployees.filter((id) => !filteredIds.has(id)));
      setSelectAllEmployees(false);
    }
  };

  // Are all currently visible (filtered) employees selected?
  const allFilteredSelected =
    filteredEmployees.length > 0 &&
    filteredEmployees.every((emp) => selectedEmployees.includes(emp._id));

  // Handle individual employee checkbox
  const handleEmployeeChange = (employeeId, checked) => {
    if (checked) {
      const newSelection = [...selectedEmployees, employeeId];
      setSelectedEmployees(newSelection);
      if (newSelection.length === employees.length) {
        setSelectAllEmployees(true);
      }
    } else {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
      setSelectAllEmployees(false);
    }
  };

  // Select all filtered employees (button action)
  const handleSelectFiltered = () => {
    const filteredIds = filteredEmployees.map((emp) => emp._id);
    const merged = [...new Set([...selectedEmployees, ...filteredIds])];
    setSelectedEmployees(merged);
    setSelectAllEmployees(merged.length === employees.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    // Validation
    if (!selectedCompanyId) {
      setValidationError("Company is required");
      return;
    }
    if (!formData.title.trim()) {
      setValidationError("Title is required");
      return;
    }
    if (!formData.nextEventDate) {
      setValidationError("Event date is required");
      return;
    }
    // Prevent creating events with past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(formData.nextEventDate + "T00:00:00");
    if (selectedDate < today) {
      setValidationError("Event date cannot be in the past");
      return;
    }
    if (!formData.startTime) {
      setValidationError("Start time is required");
      return;
    }
    if (!formData.endTime) {
      setValidationError("End time is required");
      return;
    }

    try {
      const submitData = {
        ...formData,
        company: selectedCompanyId,
        allowedEmployees: selectedEmployees,
        selectAllEmployees,
        resources,
      };

      // Determine cadence from recurrence rule
      const isRecurring = recurrenceRule.frequency !== "none";
      submitData.cadence = isRecurring ? "Custom" : "One-Time";

      // Build recurrence rule for recurring events
      if (isRecurring) {
        const { frequency, interval, daysOfWeek, endType, occurrences, endDate } = recurrenceRule;

        // Determine backend type - map frontend frequency to backend type
        let backendType = frequency;
        if (frequency === "day") {
          backendType = "daily";
        } else if (frequency === "weekly" && interval > 1) {
          backendType = "custom_interval";
        } else if (frequency === "week") {
          backendType = interval > 1 ? "custom_interval" : "weekly";
        } else if (frequency === "month") {
          backendType = "monthly";
        } else if (frequency === "year") {
          backendType = "yearly";
        }

        submitData.recurrenceRule = {
          type: backendType,
          interval: interval || 1,
          endType: endType || "after",
        };

        // Add end condition based on endType
        if (endType === "on" && endDate) {
          submitData.recurrenceRule.endDate = endDate;
        } else {
          submitData.recurrenceRule.occurrences = endType === "never" ? 52 : (occurrences || 13);
        }

        // Add days of week for weekly
        if ((frequency === "weekly" || frequency === "week") && daysOfWeek?.length > 0) {
          submitData.recurrenceRule.daysOfWeek = daysOfWeek;
        }

        // Map cadence for simple cases
        if (frequency === "weekly" && interval === 1) {
          submitData.cadence = "Weekly";
        } else if ((frequency === "monthly" || frequency === "month") && interval === 1) {
          submitData.cadence = "Monthly";
        }
      }

      if (isEditMode) {
        await updateEvent({ id: event._id, ...submitData }).unwrap();
      } else {
        await createEvent(submitData).unwrap();
      }

      onClose();
    } catch (err) {
      console.error("Failed to save event:", err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  return (
    <div className="event-form-overlay" onClick={handleOverlayClick}>
      <div className="event-form-modal">
        {/* Header */}
        <div className="event-form-header">
          <h2>{isEditMode ? "Edit Event" : "Add New Meeting"}</h2>
          <button
            type="button"
            className="event-form-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="event-form">
          {error && <ErrorMessage error={error} />}
          {validationError && (
            <div className="validation-error">{validationError}</div>
          )}

          <div className="form-group">
            <label htmlFor="title">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter event title"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter event description"
              rows={3}
            />
          </div>

          {/* Company Selection */}
          <div className="form-group">
            <label htmlFor="company">
              Company <span className="required">*</span>
            </label>
            <select
              id="company"
              name="company"
              value={selectedCompanyId}
              onChange={handleCompanyChange}
              required
            >
              <option value="">Select a company</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee Selection */}
          {selectedCompanyId && (
            <div className="form-section employee-selection">
              <div className="section-header">
                <label>Select Employees</label>
                <span className="employee-count">
                  {selectedEmployees.length} of {employees.length} selected
                </span>
              </div>

              {/* Role Filter */}
              {!employeesLoading && employees.length > 0 && availableRoles.length > 1 && (
                <div className="role-filter-row">
                  <label className="role-filter-label">Filter by Role:</label>
                  <div className="role-filter-chips">
                    <button
                      type="button"
                      className={`role-chip ${roleFilter === "all" ? "active" : ""}`}
                      onClick={() => setRoleFilter("all")}
                    >
                      All ({employees.length})
                    </button>
                    {availableRoles.map((role) => {
                      const count = employees.filter((e) => e.companyRole === role).length;
                      return (
                        <button
                          key={role}
                          type="button"
                          className={`role-chip ${roleFilter === role ? "active" : ""}`}
                          onClick={() => setRoleFilter(role)}
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="select-all-row">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={allFilteredSelected}
                  onChange={handleSelectAllChange}
                />
                <label htmlFor="selectAll">
                  {roleFilter === "all"
                    ? "Select All Employees"
                    : `Select All "${roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}" (${filteredEmployees.length})`}
                </label>
                {roleFilter !== "all" && !allFilteredSelected && filteredEmployees.length > 0 && (
                  <button
                    type="button"
                    className="select-filtered-btn"
                    onClick={handleSelectFiltered}
                  >
                    Add all {filteredEmployees.length}
                  </button>
                )}
              </div>

              {employeesLoading ? (
                <p className="loading-text">Loading employees...</p>
              ) : filteredEmployees.length > 0 ? (
                <div className="employee-list">
                  {filteredEmployees.map((emp) => (
                    <div key={emp._id} className="employee-checkbox">
                      <input
                        type="checkbox"
                        id={`emp-${emp._id}`}
                        checked={selectedEmployees.includes(emp._id)}
                        onChange={(e) =>
                          handleEmployeeChange(emp._id, e.target.checked)
                        }
                      />
                      <label htmlFor={`emp-${emp._id}`}>
                        <span className="emp-name-row">
                          <span className="emp-name">
                            {emp.firstName} {emp.lastName}
                          </span>
                          {emp.companyRole && (
                            <span className={`emp-role-badge emp-role-${emp.companyRole}`}>
                              {emp.companyRole}
                            </span>
                          )}
                        </span>
                        <span className="emp-email">{emp.email}</span>
                      </label>
                    </div>
                  ))}
                </div>
              ) : employees.length > 0 ? (
                <p className="no-employees">No employees match the selected role.</p>
              ) : (
                <p className="no-employees">No employees found for this company.</p>
              )}
            </div>
          )}

          {/* Date and time */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nextEventDate">
                {recurrenceRule.frequency === "none" ? "Event Date" : "First Event Date"}{" "}
                <span className="required">*</span>
              </label>
              <input
                type="date"
                id="nextEventDate"
                name="nextEventDate"
                value={formData.nextEventDate}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startTime">
                Start Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endTime">
                End Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <p className="form-hint">
            All dates and times are in Eastern Time (ET). Attendees see the event
            in Eastern regardless of their location.
          </p>

          {/* Recurrence Selector - Google Calendar style */}
          <div className="form-group">
            <label>Repeat</label>
            <RecurrenceSelector
              value={recurrenceRule}
              onChange={setRecurrenceRule}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="meetingPlatform">Meeting Platform</label>
              <select
                id="meetingPlatform"
                name="meetingPlatform"
                value={formData.meetingPlatform}
                onChange={handleChange}
              >
                {PLATFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="meetingLink">Meeting Link</label>
              <input
                type="url"
                id="meetingLink"
                name="meetingLink"
                value={formData.meetingLink}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Resources Section */}
          <div className="form-section">
            <div className="section-header">
              <label>Resources</label>
              <button
                type="button"
                className="add-resource-btn"
                onClick={() => setShowAddResource(true)}
              >
                <IconPlus /> Add Resource
              </button>
            </div>

            {resources.length > 0 && (
              <div className="resources-list">
                {resources.map((res, index) => (
                  <div key={index} className="resource-item">
                    <div className="resource-info">
                      <span className="resource-type-badge">
                        {getResourceIcon(res.type)}
                        {res.type}
                      </span>
                      <span className="resource-title">{res.title}</span>
                    </div>
                    <div className="resource-actions">
                      <button
                        type="button"
                        className="edit-resource-btn"
                        onClick={() => handleEditResource(index)}
                        title="Edit resource"
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="remove-resource-btn"
                        onClick={() => handleRemoveResource(index)}
                        title="Remove resource"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showAddResource && (
              <div className="add-resource-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Resource Title</label>
                    <input
                      type="text"
                      value={newResource.title}
                      onChange={(e) =>
                        setNewResource({ ...newResource, title: e.target.value })
                      }
                      placeholder="Enter title"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={newResource.type}
                      onChange={(e) =>
                        setNewResource({ ...newResource, type: e.target.value })
                      }
                    >
                      {RESOURCE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    value={newResource.url}
                    onChange={(e) =>
                      setNewResource({ ...newResource, url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>
                <div className="add-resource-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleCancelResourceEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleAddResource}
                  >
                    {editingResourceIndex !== null ? "Save" : "Add"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="event-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Event"
                : "Create Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
